import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, XCircle, Crown, Zap, ArrowRight } from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [plan, setPlan] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
      const planFromUrl = searchParams.get('plan');
      
      if (!sessionId) {
        // If coming from simple redirect without session_id
        if (planFromUrl) {
          setPlan(planFromUrl);
          setStatus('success');
          setMessage(`Parabéns! O teu plano foi atualizado para ${planFromUrl.charAt(0).toUpperCase() + planFromUrl.slice(1)}!`);
          triggerConfetti();
          refreshUser();
          return;
        }
        setStatus('error');
        setMessage('Sessão de pagamento não encontrada');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/checkout/verify/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.status === 'success') {
          setStatus('success');
          setPlan(response.data.plan);
          setMessage(response.data.message);
          triggerConfetti();
          refreshUser();
        } else if (response.data.status === 'pending') {
          setStatus('loading');
          setMessage(response.data.message);
          // Retry after 3 seconds
          setTimeout(verifyPayment, 3000);
        } else {
          setStatus('error');
          setMessage(response.data.message);
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        // If verification fails but we have plan in URL, assume success
        if (planFromUrl) {
          setPlan(planFromUrl);
          setStatus('success');
          setMessage(`Bem-vindo ao plano ${planFromUrl.charAt(0).toUpperCase() + planFromUrl.slice(1)}!`);
          triggerConfetti();
          refreshUser();
        } else {
          setStatus('error');
          setMessage('Erro ao verificar pagamento. Por favor contacta o suporte.');
        }
      }
    };

    verifyPayment();
  }, [searchParams, refreshUser]);

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#32CD32', '#FFD700', '#FF6B6B']
    });
  };

  const planFeatures = {
    pro: [
      '5 planos de treino por mês',
      '100 mensagens/dia no assistente IA',
      'Coach Virtual',
      'Vídeos de demonstração'
    ],
    premium: [
      'Planos de treino ilimitados',
      'Mensagens ilimitadas no assistente IA',
      'Biblioteca de Exercícios completa',
      'Ajuste de planos personalizado',
      'Suporte prioritário'
    ]
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Card className="bg-[#121212] border-white/10 max-w-lg w-full">
        <CardContent className="py-12 px-8">
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-[#32CD32] mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                A VERIFICAR PAGAMENTO...
              </h1>
              <p className="text-gray-400">{message || 'Por favor aguarda enquanto confirmamos o teu pagamento'}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-[#32CD32]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-[#32CD32]" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                PAGAMENTO CONFIRMADO!
              </h1>
              <p className="text-gray-400 mb-6">{message}</p>
              
              {/* Plan badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 mb-8">
                <Crown className="w-5 h-5 text-yellow-500" />
                <span className="text-yellow-500 font-bold uppercase">{plan}</span>
              </div>

              {/* Features */}
              {planFeatures[plan] && (
                <div className="text-left bg-black/30 rounded-xl p-6 mb-8">
                  <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-4">O que está incluído:</h3>
                  <ul className="space-y-3">
                    {planFeatures[plan].map((feature, index) => (
                      <li key={index} className="flex items-center gap-3 text-white">
                        <Zap className="w-4 h-4 text-[#32CD32] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/generate')}
                  className="w-full bg-[#32CD32] hover:bg-[#32CD32]/90 text-black font-bold py-6 rounded-xl"
                >
                  CRIAR PRIMEIRO PLANO
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/5 py-6 rounded-xl"
                >
                  IR PARA O DASHBOARD
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                ALGO CORREU MAL
              </h1>
              <p className="text-gray-400 mb-6">{message}</p>
              
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-[#32CD32] hover:bg-[#32CD32]/90 text-black font-bold py-6 rounded-xl"
                >
                  TENTAR NOVAMENTE
                </Button>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/5 py-6 rounded-xl"
                >
                  VOLTAR AO DASHBOARD
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
